import { noop } from '@aloft/toolkit-shared';
import { Block, Button, Flex, Typography } from '@aloft/toolkit-sui';
import { Table } from '@tanstack/react-table';

import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { selectRecommendedCompsStatusGroups } from '../../../redux/features/selectors/uiSelectors.ts';
import { toggleRecommendedCompsStatusGroup } from '../../../redux/features/uiSlice.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { scrollToIndex } from '../../ToolkitPropertyTable/utils/scrollToIndex.ts';
import { useRecCompSelectionContext } from '../RecCompSelectionContext.tsx';
import { CompSelectionProperty, CompSelectionProps } from '../types.ts';
import { getSelectedCompStats } from '../utils/compSelectionState.ts';
import { getStatusGroup } from '../utils/getStatusGroup.ts';
import { CompSelectionGrid } from './CompSelectionGrid.tsx';
import { ContextActionBar } from './ContextActionBar.tsx';
import { PropertyCompSelectionDetails } from './details/PropertyCompSelectionDetails.tsx';

const INITIAL_VISIBLE_COMPS = 20;

export const CompSelection = ({
  sorting,
  setSorting,
  rowSelection,
  setRowSelection,
  data,
  subject,
  comps,
  source,
  orderID,
  isInspectifyOrder,
  hasArvOption,
  compSelectionTableVariant = 'sales',
  isExpanded = false,
  totalComps,
  onExpand,
  onCollapse,
  updateCompsLabel,
  onUpdateComps,
  onDeselectAll,
  onFilterOpen,
  onColumnOrderChange,
  columnOrder,
  columnVisibility,
  isFilterPanelOpen,
  proximityRequirementMiles,
}: CompSelectionProps) => {
  const dispatch = useAppDispatch();
  const statusGroups = useAppSelector(selectRecommendedCompsStatusGroups);
  const compSelectionTableRef = useRef<Table<CompSelectionProperty> | undefined>();
  const [focusedRow, setFocusedRow] = useState<string | undefined>('subject');
  const [hoveredPropertyId, setHoveredPropertyId] = useState<string | undefined>();
  const [focusedRowFromPin, setFocusedRowFromPin] = useState<string | undefined>();
  const { setReferenceDrawerContent, setVisibleMapProperties } = useRecCompSelectionContext();

  const dataWithPinned = useMemo(() => [subject, ...data], [subject, data]);
  const focusedPropertyIndex = dataWithPinned.findIndex((property) => property.id === focusedRow);
  const focusedProperty =
    focusedPropertyIndex !== -1 ? dataWithPinned[focusedPropertyIndex] : undefined;

  const showFooter = totalComps !== undefined && totalComps > INITIAL_VISIBLE_COMPS;
  const hiddenCount = totalComps !== undefined ? totalComps - INITIAL_VISIBLE_COMPS : 0;

  const showPriceStats = hasArvOption && compSelectionTableVariant === 'sales';
  const selectedStats = useMemo(
    () =>
      getSelectedCompStats({
        rowSelection,
        properties: [...data, ...comps],
        isInspectifyOrder,
        hasArvOption,
      }),
    [rowSelection, data, comps, isInspectifyOrder, hasArvOption],
  );

  useLayoutEffect(() => {
    setVisibleMapProperties?.(data);
  }, [data, setVisibleMapProperties]);

  useLayoutEffect(() => () => setVisibleMapProperties?.(undefined), [setVisibleMapProperties]);

  useEffect(() => {
    if (!focusedRowFromPin) return;

    const focusedIndex = dataWithPinned.findIndex((property) => property.id === focusedRowFromPin);
    if (focusedIndex === -1) {
      setFocusedRowFromPin(undefined);
      return;
    }

    const focusedPropertyFromPin = dataWithPinned[focusedIndex];
    if (!focusedPropertyFromPin) {
      setFocusedRowFromPin(undefined);
      return;
    }

    const group = getStatusGroup(focusedPropertyFromPin.mlsStatus);
    const isCollapsed =
      compSelectionTableVariant === 'sales' &&
      focusedPropertyFromPin.id !== 'subject' &&
      group &&
      statusGroups[group].collapsed;

    if (isCollapsed) {
      dispatch(toggleRecommendedCompsStatusGroup(group));
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          scrollToIndex(focusedIndex, 'nearest');
          setFocusedRowFromPin(undefined);
        });
      });
      return;
    }

    scrollToIndex(focusedIndex, 'nearest');
    setFocusedRowFromPin(undefined);
  }, [compSelectionTableVariant, dataWithPinned, dispatch, focusedRowFromPin, statusGroups]);

  const handleFocusedRowFromPin: Dispatch<SetStateAction<string | undefined>> = useCallback(
    (value) => {
      const rowId = typeof value === 'function' ? value(focusedRow) : value;
      setFocusedRow(rowId);
      setFocusedRowFromPin(rowId);
    },
    [focusedRow],
  );

  const details = useMemo(
    () => (
      <PropertyCompSelectionDetails
        property={focusedProperty ?? subject}
        setFocusedRow={handleFocusedRowFromPin}
        hoveredPropertyId={hoveredPropertyId}
        setHoveredPropertyId={setHoveredPropertyId}
        subject={subject}
        data={data}
        comps={comps}
        compSelectionTableRef={compSelectionTableRef}
        sorting={sorting}
        rowSelection={rowSelection}
        isInspectifyOrder={isInspectifyOrder}
        hasArvOption={hasArvOption}
        compSelectionTableVariant={compSelectionTableVariant}
        isFilterPanelOpen={isFilterPanelOpen}
        proximityRequirementMiles={proximityRequirementMiles}
      />
    ),
    [
      focusedProperty,
      subject,
      handleFocusedRowFromPin,
      hoveredPropertyId,
      data,
      comps,
      sorting,
      rowSelection,
      isInspectifyOrder,
      hasArvOption,
      compSelectionTableVariant,
      isFilterPanelOpen,
      proximityRequirementMiles,
    ],
  );

  useLayoutEffect(() => {
    if (!setReferenceDrawerContent) return;
    setReferenceDrawerContent(details);
  }, [details, setReferenceDrawerContent]);

  useLayoutEffect(() => () => setReferenceDrawerContent?.(null), [setReferenceDrawerContent]);

  return (
    <Flex
      position="relative"
      flexDirection="column"
      borderWidth="1px"
      borderColor="strokeDefault"
      borderStyle="solid"
      height="100%"
    >
      <Block flex={1} minWidth={0} height="100%">
        <Flex flexDirection="column" height="100%">
          <Block flex={1} minWidth={0} overflow="hidden">
            <CompSelectionGrid
              ref={compSelectionTableRef}
              data={data}
              subject={subject}
              comps={comps}
              sorting={sorting}
              setSorting={setSorting}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              setFocusedRow={setFocusedRow}
              hoveredPropertyId={hoveredPropertyId}
              setHoveredPropertyId={setHoveredPropertyId}
              focusedPropertyIndex={focusedPropertyIndex}
              source={source}
              orderID={orderID}
              isInspectifyOrder={isInspectifyOrder}
              hasArvOption={hasArvOption}
              compSelectionTableVariant={compSelectionTableVariant}
              isExpanded={isExpanded}
              onFilterOpen={onFilterOpen}
              onColumnOrderChange={onColumnOrderChange}
              columnOrder={columnOrder}
              columnVisibility={columnVisibility}
              isFilterPanelOpen={isFilterPanelOpen}
            />
          </Block>
          {showFooter && (
            <Flex
              alignItems="center"
              gap="10px"
              padding="13px 16px"
              backgroundColor="surface1"
              flexShrink={0}
              __dangerDoNotUseSx={{
                borderTop: '1px solid',
              }}
            >
              <Button
                text={isExpanded ? 'Show less' : 'Show all'}
                variant="secondary"
                size="sm"
                __dangerDoNotUseSx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                onClick={isExpanded ? (onCollapse ?? noop) : (onExpand ?? noop)}
              />
              <Typography variant="body2" color="textSecondary">
                {isExpanded
                  ? 'All comparables shown'
                  : `${hiddenCount} of ${totalComps} comparables hidden`}
              </Typography>
            </Flex>
          )}
        </Flex>
      </Block>
      {onUpdateComps && onDeselectAll && updateCompsLabel && (
        <ContextActionBar
          selectedCount={selectedStats.selectedCount}
          showPriceStats={showPriceStats}
          aivAvgSalePrice={selectedStats.aivAvgSalePrice}
          arvAvgSalePrice={selectedStats.arvAvgSalePrice}
          priceDifference={selectedStats.difference}
          updateLabel={updateCompsLabel}
          onUpdate={onUpdateComps}
          onDeselectAll={onDeselectAll}
        />
      )}
    </Flex>
  );
};
